import { create } from 'zustand';
import { orderSettingsAPI } from '@matix/api-client';
import { useCart, type OrderMode } from './cart';

/**
 * Tenant order-settings store.
 *
 * Holds the admin-configured default order type (Settings → Order types).
 * Loaded once on cashier app boot via `loadOrderSettings()`, which also
 * pre-selects the matching order-mode pill so a fresh till lands on the
 * order type the tenant uses most.
 *
 * Fallback while loading / when unset: 'takeaway' (the former "Sales"/
 * "Walk-in" counter sale).
 */

const VALID_MODES: readonly OrderMode[] = [
  'takeaway', 'dine-in', 'delivery', 'pickup', 'membership',
];

function coerceMode(v: string | null | undefined): OrderMode {
  return (VALID_MODES as readonly string[]).includes(v ?? '')
    ? (v as OrderMode)
    : 'takeaway';
}

interface OrderSettingsState {
  defaultOrderType: OrderMode;
  loaded: boolean;
  setDefault: (m: OrderMode) => void;
}

export const useOrderSettings = create<OrderSettingsState>((set) => ({
  defaultOrderType: 'takeaway',
  loaded: false,
  setDefault: (defaultOrderType) => set({ defaultOrderType, loaded: true }),
}));

/**
 * Fetch + cache the tenant's default order type, and apply it to the active
 * cart when it's a fresh sale. Errors are swallowed so a misconfigured/missing
 * setting never blocks the till — it just defaults to Takeaway.
 */
export async function loadOrderSettings(): Promise<void> {
  try {
    const s = await orderSettingsAPI.get();
    const mode = coerceMode(s.default_order_type);
    useOrderSettings.getState().setDefault(mode);

    // Apply to the active cart only when nothing is in progress (no lines,
    // not editing a receipt) so a late-resolving fetch never clobbers an
    // order the cashier already started.
    const cart = useCart.getState();
    if (cart.lines.length === 0 && cart.editingReceiptId == null) {
      cart.setOrderMode(mode);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[order-settings] failed to load, using Takeaway default', err);
  }
}
